"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { FileSpreadsheet, FileText, Search } from "lucide-react";
import { exportToExcel, exportToPDF } from "@/lib/excelExport";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";

interface SalesReportDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    records: Array<{
      orderId: string;
      date: string;
      customerName: string;
      serviceType: string;
      packageType: string | null;
      assignedTo: string;
      bookingRef: string | null;
      status: string;
      price: number;
      quantity: number;
      totalAmount: number;
      paymentMode: string;
      serviceProvider: string | null;
      customerType: string;
    }>;
    totals: {
      totalOrders: number;
      totalRevenue: number;
      totalQuantity: number;
    };
  };
}

export function SalesReportDashboard({ data }: SalesReportDashboardProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredRecords = data.records.filter(
    (record) =>
      record.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (record.bookingRef &&
        record.bookingRef.toLowerCase().includes(searchTerm.toLowerCase())) ||
      record.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.serviceType.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getExportData = () => {
    return data.records.map((row) => ({
      "Booking Ref": row.bookingRef || "-",
      Date: format(new Date(row.date), "dd/MM/yyyy"),
      "Customer Name": row.customerName,
      "Service Type": row.serviceType,
      "Package Type": row.packageType || "-",
      "Assign To": row.assignedTo,
      Status: row.status,
      Price: row.price,
      "Req. (Qty)": row.quantity,
      "Total Amount": row.totalAmount,
      "Payment Mode": row.paymentMode,
      "Service Provider": row.serviceProvider || "-",
      "Customer Type": row.customerType,
    }));
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), "Sales_Report", "Sales");
  };

  const handleExportPDF = () => {
    exportToPDF(getExportData(), "Sales_Report", {
      title: "Sales Report",
      subtitle: "Complete sales records for the selected period",
      dateRange: data.dateRange,
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toUpperCase()) {
      case "COMPLETED":
      case "FINISHED":
        return "default";
      case "CANCELLED":
        return "destructive";
      case "IN_PROGRESS":
        return "secondary";
      default:
        return "outline";
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.totals.totalOrders}</div>
            <p className="text-xs text-muted-foreground">In selected period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(data.totals.totalRevenue)}
            </div>
            <p className="text-xs text-muted-foreground">All sales combined</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Total Quantity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.totals.totalQuantity}
            </div>
            <p className="text-xs text-muted-foreground">Services performed</p>
          </CardContent>
        </Card>
      </div>

      {/* Sales Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Sales Records</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 w-64"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Booking Ref</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Service Type</TableHead>
                  <TableHead>Package</TableHead>
                  <TableHead>Assign To</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Customer Type</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8">
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecords.map((record) => (
                    <TableRow key={record.orderId}>
                      <TableCell className="font-medium">
                        {record.bookingRef || "-"}
                      </TableCell>
                      <TableCell>
                        {format(new Date(record.date), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell>{record.customerName}</TableCell>
                      <TableCell>{record.serviceType}</TableCell>
                      <TableCell>{record.packageType || "-"}</TableCell>
                      <TableCell>{record.assignedTo}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(record.status)}>
                          {record.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(record.price)}
                      </TableCell>
                      <TableCell className="text-right">
                        {record.quantity}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(record.totalAmount)}
                      </TableCell>
                      <TableCell>{record.paymentMode}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{record.customerType}</Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
